import queryClient from '@src/app/providers/queryClient';
import {
  publishTaskRealtimeEvent,
  TASK_SUMMARY_TOPIC,
  type TaskSummaryChangedEvent,
} from '@src/features/tasks/model';
import settings from '@src/shared/config';
import { useMidnightRefresh, useTopicRefresh } from '@src/shared/realtime';
import type { TopicMessage } from '@src/shared/websocket';
import { useCallback } from 'react';

function parseTaskSummaryEvent(
  message: TopicMessage,
): TaskSummaryChangedEvent | null {
  // The shared socket already routed by topic and parsed the frame, so only
  // the payload shape still needs checking.
  if (typeof message.reason !== 'string') return null;

  const taskId =
    typeof message.task_id === 'number' ? message.task_id : undefined;
  const changeReason =
    typeof message.change_reason === 'string'
      ? message.change_reason
      : undefined;
  return {
    reason: message.reason,
    ...(taskId === undefined ? {} : { taskId }),
    ...(changeReason === undefined ? {} : { changeReason }),
  };
}

export default function TaskSummarySocketController() {
  const refreshSummary = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['root', 'tasks', 'summary'],
    });
  }, []);
  const handleMessage = useCallback((message: TopicMessage) => {
    const event = parseTaskSummaryEvent(message);
    if (!event) return;
    if (settings.DEBUG) {
      console.info('Tasks summary changed', {
        reason: event.reason,
        taskId: event.taskId,
        changeReason: event.changeReason,
      });
    }
    publishTaskRealtimeEvent(event);
  }, []);

  useMidnightRefresh(refreshSummary);
  useTopicRefresh(TASK_SUMMARY_TOPIC, handleMessage, refreshSummary);

  return null;
}
